import React, { FC, ReactElement } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import camelCase from 'lodash/camelCase';
// Components
import { clsx } from 'clsx';
import AddressIconType from './addressTypeIcon';
import Text from '../text';
// Resources
import { AddressType } from '../../core/apiTypes';
import { useBreakpoints, WebLocation } from '../../customHooks/useBreakpoints';
import SvgIcon from '../icons/SvgIcon';
import useGlobalStyles from '../hook/useGlobalStyles';
import { IconCommon } from '../../resources/images';

interface SelectedAddressCardProps {
    title: string;
    description?: string;
    type: AddressType;
    chevron?: boolean;
    onClick?: () => void;
    disabledClick?: boolean;
}

const SelectedAddressCard: FC<SelectedAddressCardProps> = ({
    title,
    description,
    type,
    chevron = false,
    onClick,
    disabledClick = false,
}): ReactElement => {
    const classes = useStyles();
    const globalClasses = useGlobalStyles();
    const { isMobileSize } = useBreakpoints(WebLocation.FLOW);

    const addressIcon = React.useMemo(() => <AddressIconType type={type} size="medium" color="black" />, [type]);

    return (
        <>
            <ListItem id={camelCase(title)} dense disablePadding>
                <ListItemButton
                    onMouseDown={onClick}
                    disabled={disabledClick}
                    className={clsx(classes.selectedAddressCardRoot, isMobileSize && classes.paddingResponsive)}
                >
                    <ListItemIcon className={classes.icon}>{addressIcon}</ListItemIcon>
                    <div className={classes.addressContainer}>
                        <ListItemText
                            className={classes.itemText}
                            disableTypography
                            primary={<Text.Description16 text={title} className={classes.itemText} />}
                            secondary={
                                <Text.ShortRegularText14
                                    text={description}
                                    customColor="grey"
                                    className={classes.itemText}
                                />
                            }
                        />
                    </div>
                    {chevron && (
                        <>
                            <span className={globalClasses.flex} />
                            <SvgIcon icon={IconCommon.ChevronRight} color="black" fill="none" size="large" />
                        </>
                    )}
                </ListItemButton>
            </ListItem>
        </>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        selectedAddressCardRoot: {
            minHeight: 50,
            paddingLeft: theme.spacing(3.75),
            paddingRight: theme.spacing(3.75),
            paddingTop: theme.spacing(1.5),
            paddingBottom: theme.spacing(1.5),
        },
        paddingResponsive: {
            paddingRight: 20,
            paddingLeft: 20,
        },
        addressContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
        itemText: {
            margin: 0,
            padding: 0,
        },
        icon: {
            marginRight: theme.spacing(2),
            minWidth: 'unset',
            borderRadius: 8,
            backgroundColor: theme.palette.colors.background.filledForm,
            padding: 12,
        },
    }),
);

export default SelectedAddressCard;
